import { LinkButton } from "@/ui/next/LinkButton";
import Link from "@whoppah/next/link";

import { IconProfileProBuyer } from "@/assets/icons/figma/IconProfileProBuyer";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { PRO_BUYER_PROGRAM_ENABLED } from "@/config";
import {
  PRO_BUYER_NAV_LABEL,
  PRO_BUYER_ROUTE,
} from "@/features/pro-buyer-application/static/pro-buyer-application.static";
import {
  PRO_BUYER_OFFER_HREF,
  ProBuyerHeaderSurface,
} from "@/features/pro-buyer-application/utils/pro-buyer-nav.utils";
import { useProBuyerViewer } from "@/features/pro-buyer-price/hooks/useProBuyerPrice";
import { Flavor } from "@/ui/typography/Flavor";

/**
 * WHO-6571. A Pro Buyer goes to their offer, everyone else to /pro-buyer
 * (which also shows a pending or rejected application). Until `me.proBuyer`
 * is known this is the guest link; a gateway error counts as not a Pro Buyer.
 * `useProBuyerViewer` is the cached answer search and the PDP already share.
 */
const useProBuyerHeaderLink = (surface: ProBuyerHeaderSurface) => {
  const { signedIn, isProBuyer } = useProBuyerViewer();

  const handleClick = () => {
    analyticsService.trackProBuyerHeaderClick(
      surface,
      isProBuyer ? "pro_buyer" : signedIn ? "not_pro_buyer" : "guest"
    );
  };

  return {
    href: isProBuyer ? PRO_BUYER_OFFER_HREF : PRO_BUYER_ROUTE,
    handleClick,
  };
};

/**
 * Desktop bar, right of Verkoop: same size and breakpoints (icon only below
 * `lg`, like Verkoop). "Pro Buyer" is never translated.
 */
export const ProBuyerHeaderButton = () => {
  const { href, handleClick } = useProBuyerHeaderLink("desktop");

  if (!PRO_BUYER_PROGRAM_ENABLED) {
    return null;
  }

  return (
    <LinkButton
      size="large"
      href={href}
      onClick={handleClick}
      aria-label={PRO_BUYER_NAV_LABEL}
      leftIcon={<IconProfileProBuyer aria-hidden className="size-6 shrink-0" />}
      className="flex shrink-0 whitespace-nowrap border-flavour-100 bg-flavour-100 text-brand-500 hover:border-flavour-200 hover:bg-flavour-200 active:border-flavour-200 active:bg-flavour-200 [&>span>span]:hidden [&>span>span]:lg:inline"
    >
      {PRO_BUYER_NAV_LABEL}
    </LinkButton>
  );
};

/** Mobile menu line after "Sale", placed by `withProBuyerMenuItem`. */
export const ProBuyerMobileMenuItem = ({
  onClick,
}: {
  onClick?: () => void;
}) => {
  const { href, handleClick } = useProBuyerHeaderLink("mobile_menu");

  return (
    <li>
      <Link
        href={href}
        prefetch={false}
        className="flex items-center space-x-2 text-left text-brand-500"
        onClick={() => {
          handleClick();
          onClick?.();
        }}
      >
        <IconProfileProBuyer aria-hidden className="size-4 shrink-0" />
        <Flavor className="flex-1 font-bold">{PRO_BUYER_NAV_LABEL}</Flavor>
      </Link>
    </li>
  );
};
